/**
 * This file is a fix for uPlot's `numAxisSplits` function.
 * Guard against non-advancing increments and excessive tick generation.
 */

import type uPlot from 'uplot';

import {recordDatadogLogInfo} from '../../../util/datadog/datadogLogs';
import {fixedDec, incrRoundUp, roundDec} from './util';

// Tick count scales with axis pixel width: uPlot spaces ticks at least
// `space` px (50 by default) apart and our incrs step by at most 2x, so a
// W px axis yields W/100 to W/50 ticks. A 3440 px ultrawide report panel
// produced 64 ticks in production, so the cap must sit well above any
// plausible layout while still stopping runaway generation.
const MAX_AXIS_TICKS = 1000;

/**
 * Same contract as uPlot's `numAxisSplits` in
 * https://github.com/leeoniya/uPlot/blob/master/src/opts.js
 * (invoked as `axis.splits(self, i, min, max, _incr, _space, forceMin)` from uPlot.js).
 */
export function numAxisSplits(
  _self: uPlot,
  axisIdx: number,
  scaleMin: number,
  scaleMax: number,
  foundIncr: number,
  foundSpace: number,
  forceMin: boolean
) {
  const splits: number[] = [];
  const originalScaleMin = scaleMin;

  const numDec = fixedDec.get(foundIncr) || 0;

  if (!forceMin) {
    let firstSplit = roundDec(incrRoundUp(scaleMin, foundIncr), numDec);
    // fixFloat can erase meaningful digits (0.1000000014901162 -> 0.1).
    // Do not let that cleanup introduce millions of ticks below a narrow domain.
    if (firstSplit < scaleMin) {
      firstSplit = roundDec(incrRoundUp(scaleMin, foundIncr, false), numDec);
    }
    scaleMin = firstSplit;
  }

  // `val + foundIncr` can round to `val` (e.g. 1 + 1e-16 === 1); without a guard the loop never advances.
  let val = scaleMin;
  while (val <= scaleMax) {
    // Omit ticks on overflow rather than render a misleading partial axis.
    if (splits.length >= MAX_AXIS_TICKS) {
      recordDatadogLogInfo('uplot_axis_tick_limit_exceeded', {
        axisIdx,
        originalScaleMin,
        scaleMin,
        scaleMax,
        foundIncr,
        foundSpace,
        forceMin,
        numDec,
        maxTicks: MAX_AXIS_TICKS,
        generatedTickCount: splits.length,
        lastTick: splits[splits.length - 1],
        nextTick: val,
      });
      return [];
    }
    splits.push(Object.is(val, -0) ? 0 : val); // coalesces -0
    const next = roundDec(val + foundIncr, numDec);
    const stuck =
      foundIncr === 0 || (foundIncr > 0 ? next <= val : next >= val);
    if (stuck) {
      break;
    }
    val = next;
  }

  return splits;
}

/** @see numAxisSplits — packaged uPlot typings omit the 7th `forceMin` argument. */
export const numAxisSplitsForUPlotAxis = numAxisSplits as uPlot.Axis.Splits;
